/**
 * Strom einer Wohneinheit — die Sparten-Seite der Wohnung (HM-083), gebaut wie
 * „Strom & Erzeugung" der Anlage (`routes/electricity.tsx`): die Karte
 * „Verbrauch" mit der Leistung jetzt (HM-156), der Verlauf mit dem Umschalter
 * Energie/Leistung, die Verwendung als Ring, zuletzt die Zähler. Seit die
 * Übersicht entfallen ist, ist sie die Startseite der Wohnung.
 *
 * **Der Verlauf hat eine Ansicht und nicht vier** (HM-093). Er hatte bis dahin
 * denselben Umschalter wie die Anlage — „Wohnung", „Verbrauch", „PV-Anteil",
 * „Netzanteil" —, und die letzten drei zeigten je einen Teil dessen, was der
 * Stapel ohnehin zeigt: PV-Anteil und Netzanteil übereinander **sind** der
 * Verbrauch. An der Anlage trennt der Umschalter Größen, die nichts
 * miteinander zu tun haben (Netz, Erzeugung, Allgemeinstrom); hier zerlegte er
 * eine Summe in ihre Summanden und nahm dabei genau die Aussage weg, wegen der
 * man hinsieht — wie viel davon aus der Anlage kam.
 *
 * **Dieselbe Form, andere Größen.** Eine Wohnung hat keine Erzeugung und keine
 * Einspeisung; sie hat Verbrauch, PV-Anteil und Netzanteil (HM-080). Der
 * PV-Anteil ist eine Rechnung über das ganze Haus (OQ-042) und heißt deshalb
 * nicht „Eigenverbrauch" — er trägt nur dessen Farbe (ADR 0013).
 *
 * **Die Leistung ist die des Zählers der Wohnung**, nicht eine Größe des
 * Konzepts — wie an der Anlage. **Die Momentanwerte kommen aus einem eigenen
 * Endpunkt** (`GET /site-areas/{id}/live`), der die Zähler mitliefert: Ein
 * Bewohner darf die Zählerliste der Anlage nicht lesen.
 *
 * **Der Anteil der PV am Verbrauch ist ein Verhältnis der Summen** (ADR 0025)
 * — aus den beiden Summen dieser Antwort, nicht aus den Viertelstunden.
 *
 * Beide Rahmen — die Wohneinheit des Vermieters und die Wohnung des Bewohners
 * — zeigen diese Komponente; was der Server über `occupancy` und `clipped`
 * sagt, erklärt sie.
 */
import { useState } from "react";
import { Alert, Badge, Card, EmptyState, SegmentedControl, icons, quantityColorVar, useNarrow } from "@hausmeter/ui";
import { Chart, type ChartSeries } from "@hausmeter/ui/chart";
import { formatValue } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";
import { ConsumptionComposition } from "../components/consumption-composition.js";
import { Donut } from "../components/donut.js";
import { KeyFigureCard, KeyFigureRow, ShareBar } from "../components/key-figures.js";
import { PERIOD_CAPTIONS } from "../components/period-controls.js";
import { PvAvailabilityCard } from "../components/pv-availability.js";
import { isOutsideOccupancy, UnitNoConcept, UnitOutsideOccupancy } from "../components/unit-states.js";
import { chartPoint, ENERGY_FORMAT, lessCertainQuality, totalNote, ENERGY_UNIT, formatEnergy, formatPercent, POWER_FORMAT, POWER_UNIT } from "../lib/energy-format.js";
import { unitConsumptionSplit } from "../lib/consumption-split.js";
import { compositionLive } from "../lib/composition-live.js";
import { formatDate, formatTimeAgo } from "../lib/format.js";
import { meterKindLabel, registerRoleLabel, meterNumberText, directedPower } from "../lib/meter-labels.js";
import type { EnergyPeriod } from "../lib/router.js";
import { useUnitEnergy, useUnitLive, useUnitPower } from "../lib/queries.js";
import { distributionExplanation, distributionLabel } from "../lib/distribution.js";
import { NEW_VALUES_DELAY } from "../lib/first-values.js";
import { LegendRow, RatioBlock } from "./electricity.js";

type UnitEnergy = ApiComponents["schemas"]["UnitEnergyResponse"];
type Total = ApiComponents["schemas"]["SiteEnergyTotal"];
type QuantityKey = Total["quantityKey"];
type Power = ApiComponents["schemas"]["SitePowerResponse"];
type UnitLive = ApiComponents["schemas"]["UnitLiveResponse"];

const MUTED = { color: "var(--text-muted)", fontSize: 12.5, lineHeight: 1.55, margin: 0 } as const;

type Mode = "energie" | "leistung";

/**
 * Die Reihen des Verlaufs: der Stapel aus PV- und Netzanteil — zusammen der
 * Verbrauch.
 *
 * **Der Verbrauch wird nicht danebengelegt**, obwohl er als Größe vorliegt: Er
 * ist die Höhe des Stapels, und eine vierte Reihe über der Summe zweier
 * anderer sähe aus wie ein dritter Anteil.
 */
const STACK: readonly QuantityKey[] = ["unit_pv_share", "unit_grid_share"];

export function UnitElectricityView({
  siteAreaId,
  unitName,
  period,
  date,
  timeZone,
  onConcept,
}: {
  siteAreaId: string;
  unitName: string;
  period: EnergyPeriod;
  date: string | null;
  /** Die Zeitzone der Anlage, nie die des Browsers. */
  timeZone: string;
  onConcept?: () => void;
}) {
  const [mode, setMode] = useState<Mode>("energie");
  const query = { period, date };
  const energy = useUnitEnergy(siteAreaId, query);
  const power = useUnitPower(siteAreaId, query, mode === "leistung");
  const live = useUnitLive(siteAreaId);

  if (energy.isPending) return <p style={MUTED}>Wird geladen …</p>;
  if (energy.isError || energy.data === undefined) {
    return <Alert tone="danger">Die Werte der Wohneinheit konnten nicht geladen werden.</Alert>;
  }
  const data: UnitEnergy = energy.data;
  if (data.conceptId === null) return <UnitNoConcept title="Strom" onConcept={onConcept} />;
  if (isOutsideOccupancy(data)) return <UnitOutsideOccupancy title="Strom" data={data} />;

  return (
    <EnergyView
      energy={data}
      power={power.data ?? null}
      powerPending={power.isPending}
      live={live.data ?? null}
      mode={mode}
      onMode={setMode}
      period={period}
      timeZone={timeZone}
      unitName={unitName}
    />
  );
}

function EnergyView({
  energy,
  power,
  powerPending,
  live,
  mode,
  onMode,
  period,
  timeZone,
  unitName,
}: {
  energy: UnitEnergy;
  power: Power | null;
  powerPending: boolean;
  live: UnitLive | null;
  mode: Mode;
  onMode: (mode: Mode) => void;
  period: EnergyPeriod;
  timeZone: string;
  unitName: string;
}) {
  const narrow = useNarrow(1040);
  const [view, setView] = useState<"herkunft" | "verbrauch">("herkunft");

  const totalOf = (key: QuantityKey): Total | undefined => energy.totals.find((t) => t.quantityKey === key);
  const seriesOf = (key: QuantityKey) => energy.series.find((s) => s.quantityKey === key);
  const has = (key: QuantityKey): boolean => energy.series.some((s) => s.quantityKey === key);
  /* Führt das Konzept die beiden Anteile nicht, bleibt der Verbrauch allein —
     eine Wohnung ohne Mieterstrom hat nichts aufzuteilen. Und liefert es auch
     den nicht, wird nichts gezeichnet statt eine leere Achse hingestellt. */
  const stack = STACK.every(has);
  const quantities: readonly QuantityKey[] = stack
    ? STACK
    : has("unit_consumption")
      ? (["unit_consumption"] as const)
      : [];
  /* Beim Summenzählermodell gibt es die Anteile nur als Summe (HM-097): Der
     Verlauf zeigt den Verbrauch, die Kacheln daneben den Anteil des Zeitraums. */
  const sumModel = energy.distribution === "annual_sum";
  const chartTitle = stack ? "Wohnung" : "Verbrauch";
  const chartSubtitle = stack
    ? "PV-Anteil und Netzanteil, zusammen der Verbrauch"
    : sumModel
      ? "Was die Wohnung verbraucht hat — der PV-Anteil entsteht beim Summenzählermodell nur als Summe"
      : "Was die Wohnung verbraucht hat";
  /* Die Beschriftung einer Größe trägt den Namen der Wohnung („Verbrauch Wohnung 2");
     auf der Seite der Wohnung steht er in der Kopfleiste und wäre hier doppelt. */
  const short = (label: string): string => label.replace(` ${unitName}`, "");

  /**
   * **Die zweite Ansicht zerlegt den Verbrauch nach Gegenstand, nicht nach
   * Herkunft** (HM-118). HM-093 hat den Umschalter entfernt, weil er eine Summe
   * in ihre Summanden zerlegte — PV-Anteil und Netzanteil *sind* der Verbrauch.
   * „Verbrauch" ist eine andere Frage an dieselbe Höhe: Wallbox, Wärmepumpe und
   * übriger Verbrauch übereinander. Nur als Energie: Die Leistung eines
   * Verbrauchers ist eine eigene Messung und keine Aufteilung dieser Kurve.
   */
  const split = unitConsumptionSplit(energy);
  const byConsumer = mode === "energie" && view === "verbrauch" && split !== null && split.stackable;

  const from = new Date(energy.from).getTime();
  const to = new Date(energy.to).getTime();

  /*
   * **Die Leistung jetzt je Teil** (HM-149): vom Server nach der Regel des
   * Messkonzepts gerechnet (`allocation.outputs`) — auch für einen Bewohner,
   * der die übrigen Zähler nicht sehen darf. Nur, solange der Zeitraum das
   * Jetzt enthält; fehlt einem Verbraucher der Wert, für keinen Teil.
   */
  const nowMs = Date.now();
  const outputs = live?.allocation?.outputs ?? null;
  const splitNow =
    split === null || outputs === null || !(from <= nowMs && nowMs < to)
      ? null
      : compositionLive(
          split.parts.map((part) => ({ key: part.key, rest: part.kind === "rest" })),
          (key) =>
            outputs.find((o) => o.quantityKey === "load_consumption" && o.subjectKey === key)
              ?.watts ?? null,
          live?.allocation?.consumptionW ?? null,
          {
            /* Woher (HM-150): der PV-Anteil je Verbraucher, der Rest aus dem der Wohnung. */
            of: (key) =>
              outputs.find((o) => o.quantityKey === "load_pv_share" && o.subjectKey === key)
                ?.watts ?? null,
            totalW: live?.allocation?.pvShareW ?? null,
          },
        );
  const caption = PERIOD_CAPTIONS[period];
  const empty = energy.series.every((s) => s.points.length === 0);

  const consumption = totalOf("unit_consumption");
  const pv = totalOf("unit_pv_share");
  const grid = totalOf("unit_grid_share");

  /**
   * Der Anteil der PV am Verbrauch — ein Verhältnis der Summen (ADR 0025),
   * `null`, wo eine der beiden fehlt oder der Verbrauch null ist: Ohne
   * Verbrauch gibt es keinen Anteil, und `0 %` wäre eine Aussage.
   */
  const pvRatio: Total | null =
    pv === undefined || consumption === undefined
      ? null
      : {
          quantityKey: "unit_pv_share",
          label: "PV-Anteil am Verbrauch",
          unit: "ratio",
          value: pv.value === null || consumption.value === null || consumption.value === 0 ? null : pv.value / consumption.value,
          coverage: Math.min(pv.coverage, consumption.coverage),
          quality: lessCertainQuality(pv.quality, consumption.quality),
        };

  /* Die Momentanwerte oben rechts in der Karte, wie an der Anlage (HM-089):
     die Leistung der Wohnung, PV-Anteil und Netzanteil der Leistung in ihren
     Zeilen — nach der Regel des Konzepts, wenn der Server sie liefert; sonst
     nur die Leistung des Zählers. */
  const nowPower = (live?.values ?? []).filter((v) => v.role === "power_total" && v.unit === "W" && v.ageSeconds <= 5 * 60);
  const allocation = live?.allocation ?? null;
  /* Ohne Aufteilung die Leistung **des** Zählers — und nur, wenn es einer ist: Ein
     zweiter an der Wohnung hängt seit HM-111 meist hinter dem ersten (die Wallbox),
     und die Summe zählte ihn doppelt. */
  const nowWatts = allocation?.consumptionW ?? (nowPower.length === 1 ? nowPower[0]!.value : null);
  /**
   * **Die Karte „Verbrauch" des Entwurfs** (HM-156, `components/key-figures.tsx`)
   * statt dreier Kacheln — dieselbe wie an der Anlage, mit PV-Anteil und
   * Netzanteil als Teilen. Der Anteil neben dem Namen ist das Verhältnis der
   * Summen (`pvRatio`), der Netzanteil sein Rest.
   */
  const pvShare = pvRatio?.value ?? null;
  const withNow = nowWatts !== null || allocation !== null;

  /**
   * **„Dein Strom vom Haus" neben der Karte „Verbrauch"** (HM-166, Entwurf 3s):
   * ob gerade PV-Strom frei ist. Nur mit einer Aufteilung jetzt und einer
   * Einspeisung im Konzept — beim Summenzählermodell gibt es keinen Anteil
   * jetzt, und ohne ihn keine Aussage. „Jetzt" hängt nicht am Zeitraum.
   * Schmal steht sie oben: Sie beantwortet die Frage, wegen der man das
   * Telefon herausholt.
   */
  const availability = allocation?.pvAvailability ?? null;
  const availabilityCard =
    availability === null || allocation === null ? null : (
      <PvAvailabilityCard value={availability} consumptionW={allocation.consumptionW} />
    );
  const consumptionCard = consumption !== undefined && (
    <KeyFigureCard
      label={short(consumption.label)}
      nowW={nowWatts}
      totalWh={consumption.value}
      color={quantityColorVar("unit_consumption")}
      note={totalNote(consumption) ?? undefined}
    >
      <ShareBar
        parts={
          pvShare === null
            ? null
            : [
                { share: pvShare, color: quantityColorVar("unit_pv_share") },
                { share: 1 - pvShare, color: quantityColorVar("unit_grid_share") },
              ]
        }
      />
      {pv !== undefined && (
        <KeyFigureRow
          live={withNow}
          color={quantityColorVar("unit_pv_share")}
          label={short(pv.label)}
          share={pvShare}
          nowW={allocation?.pvShareW ?? null}
          wh={pv.value}
        />
      )}
      {grid !== undefined && (
        <KeyFigureRow
          live={withNow}
          color={quantityColorVar("unit_grid_share")}
          label={short(grid.label)}
          share={pvShare === null ? null : 1 - pvShare}
          nowW={allocation?.gridShareW ?? null}
          wh={grid.value}
        />
      )}
    </KeyFigureCard>
  );

  /**
   * **Keine Werte: eine Fläche statt einer Seite voller Striche** (HM-183,
   * UI-Durchsicht U4). Dort stand „– kWh“ an jeder Karte und „bei 0 % Abdeckung“
   * darunter — für einen Mieter liest sich das wie ein Schaden. Der Satz sagt,
   * woran es liegt: vor dem Einzug, oder der Zähler hat noch nichts gemeldet.
   */
  if (empty) {
    const beforeMoveIn = energy.occupancy !== null && energy.clipped;
    return (
      <Card>
        <EmptyState title="Für diesen Zeitraum liegen keine Werte vor" icon={<icons.IconActivity size={30} />} size="md">
          {beforeMoveIn
            ? `Du siehst die Werte ab deinem Einzug am ${formatDate(energy.occupancy!.movedInOn)}.`
            : "Der Zähler der Wohnung hat in dieser Zeit nichts gemeldet, oder er ist noch nicht im Messkonzept des Hauses eingetragen. " + NEW_VALUES_DELAY}
        </EmptyState>
      </Card>
    );
  }

  return (
    <>
      {availabilityCard === null ? (
        consumptionCard
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: 16 }}>
          {narrow && availabilityCard}
          {consumptionCard}
          {!narrow && availabilityCard}
        </div>
      )}

      {energy.occupancy !== null && energy.clipped && (
        <Alert tone="info">
          Du siehst die Werte ab deinem Einzug am {formatDate(energy.occupancy.movedInOn)}
          {energy.occupancy.movedOutOn === null ? "" : ` bis zu deinem Auszug am ${formatDate(energy.occupancy.movedOutOn)}`}.
        </Alert>
      )}

      <Card
        title={byConsumer ? "Verbrauch" : chartTitle}
        subtitle={`${byConsumer ? "Nach Verbraucher aufgeteilt" : chartSubtitle} · ${caption} · ${mode === "leistung" ? POWER_UNIT : ENERGY_UNIT}`}
        action={
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {mode === "energie" && split !== null && split.stackable && (
              <SegmentedControl
                label="Ansicht"
                size="sm"
                value={view}
                onChange={setView}
                options={[
                  { value: "herkunft", label: "Herkunft" },
                  { value: "verbrauch", label: "Verbrauch" },
                ]}
              />
            )}
            <SegmentedControl
              label="Größe"
              size="sm"
              value={mode}
              onChange={onMode}
              options={[
                { value: "energie", label: "Energie" },
                { value: "leistung", label: "Leistung" },
              ]}
            />
          </div>
        }
      >
        {byConsumer && split !== null && (
          <>
            <Chart
              series={split.parts.map(
                (part) =>
                  ({
                    id: part.key,
                    label: part.label,
                    quantity: part.shade!,
                    mark: "bars",
                    points: part.series!.points.map(chartPoint),
                  }) satisfies ChartSeries,
              )}
              from={from}
              to={to}
              bucket={energy.resolution}
              mergeHours
              format={ENERGY_FORMAT}
              timeZone={timeZone}
              height={230}
              stacked
              stackTotalLabel="Verbrauch"
              legend
              label={`Verbrauch ${unitName} nach Verbraucher, ${caption}`}
              emptyMessage="Im Zeitraum liegen keine Werte vor."
            />
            <p style={{ ...MUTED, marginTop: 10 }}>
              Gestapelt, nicht gruppiert: Die Streifen sind Teile eines Ganzen, die Höhe eines Balkens
              bleibt der Verbrauch der Wohnung. Der oberste Streifen ist der gerechnete Rest.
            </p>
          </>
        )}
        {!byConsumer && mode === "energie" && quantities.length > 0 && (
          <Chart
            series={quantities.map((key) => {
              const entry = seriesOf(key)!;
              return {
                id: entry.quantityKey,
                label: short(entry.label),
                quantity: entry.quantityKey,
                mark: "bars",
                points: entry.points.map(chartPoint),
              } satisfies ChartSeries;
            })}
            from={from}
            to={to}
            bucket={energy.resolution}
            mergeHours
            format={ENERGY_FORMAT}
            timeZone={timeZone}
            height={230}
            stacked={stack}
            /* Die beiden Anteile übereinander sind der Verbrauch — im
               Ablesefenster steht die Summe deshalb unter ihrem eigenen Namen
               und nicht an der oberen Reihe. */
            stackTotalLabel="Verbrauch"
            legend
            label={`${chartTitle} ${unitName}, ${caption}`}
            emptyMessage="Im Zeitraum liegen keine Werte vor."
          />
        )}
        {mode === "energie" && quantities.length === 0 && (
          <p style={MUTED}>Das Messkonzept liefert für diese Wohnung keine Größe, die sich hier zeichnen ließe.</p>
        )}
        {mode === "leistung" && (
          <PowerChart
            power={power}
            pending={powerPending}
            quantities={quantities}
            stack={stack}
            short={short}
            unitName={unitName}
            timeZone={timeZone}
            caption={caption}
          />
        )}
      </Card>

      {pv !== undefined && grid !== undefined && (
        <div style={{ display: "grid", gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: 16 }}>
          <Card title="Herkunft" subtitle="Woher kam der Strom der Wohnung?">
            <div style={{ display: "flex", alignItems: "center", gap: 22, paddingTop: 4, flexWrap: "wrap" }}>
              <Donut
                segments={[
                  { value: pv.value ?? 0, color: "var(--hm-quantity-self-consumption)", label: "PV-Anteil" },
                  { value: grid.value ?? 0, color: "var(--hm-quantity-grid-import)", label: "Netzanteil" },
                ]}
                centerLabel={pvRatio?.value == null ? "–" : formatPercent(pvRatio.value)}
                centerSub="aus der Anlage"
              />
              <div style={{ flex: 1, minWidth: 160, display: "flex", flexDirection: "column", gap: 10 }}>
                <LegendRow color="var(--hm-quantity-self-consumption)" label="PV-Anteil" total={pv} />
                <LegendRow color="var(--hm-quantity-grid-import)" label="Netzanteil" total={grid} />
                <LegendRow color="var(--hm-quantity-consumption)" label="Verbrauch" total={consumption} />
              </div>
            </div>
            {pvRatio !== null && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border-subtle)", display: "flex", gap: 20, flexWrap: "wrap" }}>
                <RatioBlock
                  label="PV-Anteil am Verbrauch"
                  total={pvRatio}
                  color="var(--hm-quantity-self-consumption)"
                  formula="PV-Anteil ÷ Verbrauch"
                  note="des Verbrauchs aus der Anlage gedeckt"
                />
              </div>
            )}
          </Card>

          <Card
            title="Wie der Anteil entsteht"
            subtitle={energy.distribution === null ? "Eine Rechnung über das ganze Haus" : `${distributionLabel(energy.distribution)} · eine Rechnung über das ganze Haus`}
          >
            <p style={MUTED}>{distributionExplanation(energy.distribution ?? "dynamic")}</p>
          </Card>
        </div>
      )}

      {split !== null && (
        <ConsumptionComposition split={split} caption={caption} live={splitNow} />
      )}

      <MetersCard live={live} />
    </>
  );
}

/**
 * Die Leistung der Wohnung — **dieselben Größen wie ihr Energieverlauf** (HM-095),
 * nur in Kilowatt und als Fläche statt als Balken: PV-Anteil und Netzanteil
 * übereinander, zusammen der Verbrauch.
 *
 * **Nicht mehr die rohe Linie ihres Zählers.** Die zeigte, was der Zähler
 * meldet, und ließ die Frage offen, wegen der man hinsieht — wie viel davon
 * gerade aus der Anlage kommt. Das rechnet das Messkonzept, in Watt wie in
 * Wattstunden; der Server tut es über das ganze Haus und gibt nur die Größen
 * dieser Wohnung heraus (HM-088).
 */
function PowerChart({
  power,
  pending,
  quantities,
  stack,
  short,
  unitName,
  timeZone,
  caption,
}: {
  power: Power | null;
  pending: boolean;
  quantities: readonly QuantityKey[];
  stack: boolean;
  short: (label: string) => string;
  unitName: string;
  timeZone: string;
  caption: string;
}) {
  if (pending || power === null) return <p style={MUTED}>Die Leistung wird geladen …</p>;
  const series = quantities
    .map((key) => power.series.find((entry) => entry.quantityKey === key))
    .filter((entry) => entry !== undefined);
  if (series.length === 0) {
    return (
      <p style={MUTED}>
        Kein Zähler des Messkonzepts hat ein Leistungszählwerk mit Quelle. Die Leistung meldet ein
        Gerät je Zähler als eigenen Wert; gebunden wird sie unter „Zähler".
      </p>
    );
  }
  return (
    <>
      <Chart
        series={series.map((entry) => ({
          id: entry.quantityKey,
          label: short(entry.label),
          quantity: entry.quantityKey,
          mark: stack ? "area" : "line",
          points: entry.points.map(chartPoint),
        }))}
        from={new Date(power.from).getTime()}
        to={new Date(power.to).getTime()}
        bucket={power.resolution}
        format={POWER_FORMAT}
        timeZone={timeZone}
        height={230}
        stacked={stack && series.length > 1}
        stackTotalLabel="Verbrauch"
        legend
        label={`Leistung ${unitName}, ${caption}`}
        emptyMessage="Im Zeitraum liegen keine Leistungswerte vor."
      />
      <p style={{ ...MUTED, marginTop: 10 }}>
        Die mittlere Leistung je Zeitschritt, nach der Regel des Messkonzepts aufgeteilt — wie
        die Energie daneben. Fehlt in einem Schritt die Leistung eines beteiligten Zählers des
        Hauses, fehlt der Anteil: Eine halbe Bilanz ist keine.
      </p>
    </>
  );
}

/**
 * Die Zähler der Wohnung mit Leistung **und Zählerstand** (HM-089), je mit Alter.
 * Der Stand bleibt in kWh, auch wenn er groß wird: Auf dem Zähler stehen
 * 11062,958 kWh, nicht „11,1 MWh" — mit einer gerundeten Megawattstunde lässt
 * sich nichts vergleichen.
 */
function MetersCard({ live }: { live: UnitLive | null }) {
  const meters = live?.meters ?? [];
  const values = live?.values ?? [];
  /* Das Wort für den Stand kommt aus dem Messkonzept (HM-090): „Verbrauch" am
     Wohnungszähler, „Abgabe ins Haus" an der Eigen-Wohnung; ohne Konzept die Rolle. */
  const readingLabel = (meter: UnitLive["meters"][number], role: string): string =>
    meter.registers.find((entry) => entry.role === role)?.label ?? registerRoleLabel(role as Parameters<typeof registerRoleLabel>[0]);
  return (
    <Card title="Zähler" subtitle="Die Zähler an den Messstellen dieser Wohnung">
      {live === null && <p style={MUTED}>Wird geladen …</p>}
      {live !== null && meters.length === 0 && (
        <p style={MUTED}>An dieser Wohnung hängt noch kein Zähler. Zugeordnet wird er in seinen Stammdaten oder im Messkonzept.</p>
      )}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {meters.map((meter, index) => {
          const power = values.find((v) => v.meterId === meter.id && v.role === "power_total") ?? null;
          const stale = power !== null && power.ageSeconds > 10 * 60;
          const readings = values.filter((v) => v.meterId === meter.id && v.unit === "Wh" && v.role.startsWith("energy_"));
          return (
            <div
              key={meter.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "13px 0",
                borderBottom: index < meters.length - 1 ? "1px solid var(--border-subtle)" : "none",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  width: 34,
                  height: 34,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 9,
                  background: "color-mix(in srgb, var(--hm-commodity-electricity) 16%, transparent)",
                  color: "var(--text-body)",
                }}
              >
                <icons.IconGauge size={17} />
              </span>
              <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)" }}>{meter.label}</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
                  {meterNumberText(meter)} · {meterKindLabel(meter.kind)}
                </div>
              </div>
              {readings.length > 0 && (
                <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                  {readings.map((reading) => (
                    <span key={reading.role} style={{ fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums", fontSize: 13, color: "var(--text-body)", whiteSpace: "nowrap" }}>
                      <span style={{ color: "var(--text-faint)", fontSize: 11 }}>{readingLabel(meter, reading.role)} </span>
                      {formatEnergy(reading.value, 3)} {ENERGY_UNIT}
                    </span>
                  ))}
                </span>
              )}
              {power !== null && (
                <span style={{ fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums", fontSize: 16, fontWeight: 600, color: stale ? "var(--text-faint)" : "var(--text-strong)", whiteSpace: "nowrap" }}>
                  {/* Ohne Vorzeichen (UI-Durchsicht U3) — ein Wohnungszähler hängt nicht am Netzanschluss. */}
                  {formatValue(power.unit === "W" ? directedPower(power.value, false).watts : power.value, power.unit).full}
                </span>
              )}
              {power === null ? (
                <Badge tone="neutral">keine Leistung gemeldet</Badge>
              ) : stale ? (
                <Badge tone="warning">zuletzt {formatTimeAgo(power.measuredAt)}</Badge>
              ) : (
                <Badge tone="success">liefert · {formatTimeAgo(power.measuredAt)}</Badge>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
